import { MarketField } from 'components/fields/market';
import { PageHeader } from 'components/page-header';
import {
    useBooking,
    useSyncBookingWithUrl,
    useScrollToTop,
    useCaptureCountlyEvent,
    useMarkets,
    useAutocompleteScrollHandler,
} from 'hooks';
import { addGoogleTagManagerEvent } from 'modules/google-tag-manager';
import { useCallback, useEffect, useRef, useTransition } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Market } from 'types';
import { getLastBookedAppointmentFromStorage } from 'utils/appointment';

import * as Styled from './styles';

const SelectMarketPage = () => {
    useSyncBookingWithUrl();
    useScrollToTop();

    const navigate = useNavigate();
    const { search } = useLocation();
    const { addCountlyEvent } = useCaptureCountlyEvent();
    const { setBookingField } = useBooking();
    const marketFieldRef = useRef<HTMLDivElement | null>(null);
    const { data: markets, isFetching: isMarketsFetching } = useMarkets();
    const handleAutocompleteScrollOnMobile = useAutocompleteScrollHandler();
    const startTransition = useTransition()[1];

    const goToAppointmentDetailsFormPage = useCallback(() => {
        navigate({
            pathname: '/select-appointment-details',
            search,
        });
    }, [navigate, search]);

    const handleOnMarketChange = useCallback(
        (market: Market | undefined) => {
            if (market) {
                addCountlyEvent({
                    key: 'step1.selectRegion',
                    segmentation: { market: market.market_key },
                });
                addGoogleTagManagerEvent('select-market', { market });

                startTransition(() => {
                    setBookingField({ market });
                });

                goToAppointmentDetailsFormPage();
            }
        },
        [setBookingField, goToAppointmentDetailsFormPage, addCountlyEvent]
    );

    /**
     * If there is a last booked appointment in the local storage,
     * redirect to the appointment details form page.
     * skipping the market selection page.
     */
    useEffect(() => {
        const lastBookedAppointment = getLastBookedAppointmentFromStorage();
        if (lastBookedAppointment) {
            navigate({ pathname: '/select-appointment-details', search }, { replace: true });
        }
    }, [navigate, search]);

    return (
        <Styled.Wrapper>
            <Styled.Container>
                <PageHeader />
                <MarketField
                    ref={marketFieldRef}
                    data-testid="select-market"
                    label="Select region"
                    placeholder="Please select"
                    id="select-market"
                    isClearable={true}
                    markets={markets}
                    isLoading={isMarketsFetching}
                    onChange={handleOnMarketChange}
                    onFocus={handleAutocompleteScrollOnMobile(marketFieldRef)}
                />
            </Styled.Container>
        </Styled.Wrapper>
    );
};

export { SelectMarketPage };
